import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'zipInput', 'fileInfo', 'billableType', 'billableTypeGroup', 'taskCategory', 'taskCategoryGroup',
    'processButton', 'progressCard', 'progressBar', 'progressStats', 'log', 'statusBadge',
    'destinationPath', 'totalFiles', 'processedFiles', 'successCount', 'errorCount', 'elapsedTime',
    'selectDestinationButton'
  ]

  static values = {
    flightName: String,
    flightDate: String
  }

  connect () {
    this.resetState()
    this.startTime = null
    this.timerInterval = null
    this.successfulFiles = 0
    this.errorFiles = 0
    this.destinationRootHandle = null

    // Check File System Access API support
    if (!('showDirectoryPicker' in window)) {
      this.updateStatus('Browser Not Supported', 'danger')
      this.logMessage("⚠️ Your browser doesn't support the File System Access API. Please use Chrome or Edge.", 'warning')
    }
  }

  disconnect () {
    this.clearTimer()
  }

  validateForm () {
    const zipFile = this.zipInputTarget.files[0]
    const flightDate = this.parseFlightDate()
    const isValid = zipFile &&
      this.flightNameValue.trim() !== '' &&
      flightDate !== null &&
      this.billableTypeTargets.some(radio => radio.checked) &&
      this.taskCategoryTargets.some(radio => radio.checked) &&
      this.destinationRootHandle

    this.processButtonTarget.disabled = !isValid

    if (zipFile) {
      const sizeInMB = (zipFile.size / (1024 * 1024)).toFixed(2)
      this.fileInfoTarget.textContent = `${zipFile.name} (${sizeInMB} MB)`
    } else {
      this.fileInfoTarget.textContent = ''
    }
  }

  async selectDestination () {
    try {
      this.updateStatus('Selecting Destination', 'info')
      const rootHandle = await window.showDirectoryPicker()
      this.destinationRootHandle = rootHandle

      this.destinationPathTarget.textContent = rootHandle.name

      this.logMessage(`Destination root folder "${rootHandle.name}" selected`, 'info')
      this.validateForm()
    } catch (error) {
      this.destinationRootHandle = null
      this.destinationPathTarget.textContent = ''
      this.updateStatus('Destination Not Selected', 'danger')
      this.logMessage('Destination folder selection cancelled.', 'warning')
      this.validateForm()
    }
  }

  clearZipInput () {
    this.zipInputTarget.value = ''
    this.fileInfoTarget.textContent = ''
    this.validateForm()
  }

  resetForm () {
    if (this.processing && !confirm('Processing is in progress. Are you sure you want to reset?')) {
      return
    }
    this.clearZipInput()
    this.resetState()
    this.logMessage('Form has been reset.', 'info')
  }

  clearLog () {
    this.logTarget.textContent = ''
  }

  // Check if file should be skipped (system files, metadata)
  shouldSkipFile (filename) {
    // Skip macOS and metadata files
    if (
      filename.startsWith('__MACOSX/') ||
      filename.includes('/._') ||
      filename.startsWith('._') ||
      filename.endsWith('.DS_Store') ||
      filename.includes('/.DS_Store') ||
      filename.endsWith('Thumbs.db') ||
      filename.endsWith('desktop.ini') ||
      filename.includes('/Thumbs.db') ||
      filename.includes('/desktop.ini')
    ) {
      this.logMessage(`Skipping macOS system file: ${filename}`, 'info')
      return true
    }
    return false
  }

  // Sanitize filename to ensure valid characters
  sanitizeFileName (filename) {
    const sanitized = filename
      .replace(/[\\/:*?"<>|]/g, '_')
      .replace(/\s+/g, '_')
      .replace(/__+/g, '_')

    // Limit length to 255 characters as some filesystems have this limit
    return sanitized.substring(0, 255)
  }
  // === Source Folder and File name ===
  // "OneFolderName" (The root folder)
  // - "2025-05-02-10250 2025-05-02 08/56/35 (UTC+10)" (The lv1 subfolder)
  // - - DJI_2025_05_02_09_57_27_7CTDM2100BQ58L.DAT
  // - - Remote-Control (The lv2 subfolder)
  // - - - DJI_20250502100031_0001_V.jpeg
  // - - - DJI_20250502100033_0002_D.MRK
  // - - - DJI_20250502100033_0003_V.jpeg

  // === Target folder/files ===
  // 1. Create a folder with the name format:
  //    YYYY-MM-DD-FlightName-BillableType-TaskCategory
  // 2. Inside the folder, rename files to:
  //    - image/video files need time part extracted from the filename, or exif data:
  //      YYYY-MM-DD--HH-MM-SS-FlightName-BillableType-TaskCategory.ext
  //    - other format:
  //      YYYY-MM-DD--FlightName-BillableType-TaskCategory.ext
  // 3. If a file with the same name exists, append a counter before flight name to the filename:
  //    YYYY-MM-DD--HH-MM-SS(2)-FlightName-BillableType-TaskCategory.ext
  async processFiles () {
    const zipFile = this.zipInputTarget.files[0]
    if (!zipFile) {
      this.showError('Please select a ZIP file.')
      return
    }
    if (!this.destinationRootHandle) {
      this.showError('Please select a destination folder first.')
      return
    }

    const flightName = this.flightNameValue.trim()
    const billableType = this.getSelectedRadioValue(this.billableTypeTargets)
    const taskCategory = this.getSelectedRadioValue(this.taskCategoryTargets)
    const flightDate = this.parseFlightDate()

    if (!flightName) {
      this.showError('Flight name cannot be empty.')
      return
    }

    if (!flightDate) {
      this.showError('Flight date is invalid or empty.')
      return
    }

    // Disable form during processing
    this.setProcessingState(true)
    this.processing = true
    this.startTimer()

    try {
      // ======================
      // ===== 1. Prepare =====
      // ======================
      this.progressCardTarget.style.display = 'block'
      this.updateStatus('Preparing Process', 'info')

      // Load libraries
      this.updateStatus('Loading Libraries', 'info')
      const { ZipReader, BlobReader, BlobWriter } = await import('@zip.js/zip.js')

      // Read ZIP file
      this.updateStatus('Reading ZIP', 'info')
      const reader = new ZipReader(new BlobReader(zipFile))
      const allEntries = await reader.getEntries()

      // Filter out OS system files and directories
      const entries = allEntries.filter(entry => !this.shouldSkipFile(entry.filename))

      // Reset counters
      this.successfulFiles = 0
      this.errorFiles = 0

      // Progress tracking
      const totalEntries = entries.length
      this.totalFilesTarget.textContent = totalEntries
      this.logMessage(`Found ${totalEntries} valid entries in the ZIP file (excluded ${allEntries.length - totalEntries} system files).`, 'info')

      // ===================================
      // ===== 2. Create Target Folder =====
      // ===================================
      const targetFolderName = this.buildTargetFolderName(entries, flightDate, flightName, billableType, taskCategory)
      const rootHandle = this.destinationRootHandle

      this.updateStatus('Creating Folder', 'primary')
      this.logMessage(`Creating destination folder: ${targetFolderName}`, 'info')
      this.destinationPathTarget.textContent = targetFolderName
      const subFolder = await rootHandle.getDirectoryHandle(targetFolderName, { create: true })

      // ===================================
      // ===== 3. Create Target Files =====
      // ===================================
      this.updateStatus('Creating Files', 'primary')

      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i]
        try {
          if (entry.directory) {
            // Skip directories, flatten all files
            this.updateProgress(i + 1, totalEntries)
            continue
          }

          this.logMessage(`Processing: ${entry.filename}`, 'info')
          const uniqueFileName = await this.buildTargetFileName(entry, flightDate, flightName, billableType, taskCategory, subFolder)

          // Save the file
          const fileHandle = await subFolder.getFileHandle(uniqueFileName, { create: true })
          const writable = await fileHandle.createWritable()
          const blob = await entry.getData(new BlobWriter())
          await writable.write(blob)
          await writable.close()

          this.logMessage(`✅ Saved: ${uniqueFileName}`, 'success')
          this.successfulFiles++
          this.successCountTarget.textContent = this.successfulFiles
        } catch (err) {
          this.logMessage(`❌ Error processing ${entry.filename}: ${err?.message || err}`, 'error')
          this.errorFiles++
          this.errorCountTarget.textContent = this.errorFiles
        }
        this.updateProgress(i + 1, totalEntries)
      }

      await reader.close()

      // ===============================
      // ===== 4. Finalize Process =====
      // ===============================
      const status = this.errorFiles > 0 ? 'Completed with Errors' : 'Completed Successfully'
      const statusType = this.errorFiles > 0 ? 'warning' : 'success'
      this.updateStatus(status, statusType)
      this.logMessage(`💯 All files processed! Success: ${this.successfulFiles}, Errors: ${this.errorFiles}`, 'info')
    } catch (error) {
      this.updateStatus('Failed', 'danger')
      this.logMessage(`❌ Error: ${error.message}`, 'error')
      alert(`An error occurred: ${error.message}`)
    } finally {
      this.stopTimer()
      this.setProcessingState(false)
      this.processing = false
    }
  }

  // Check if file already exists in directory
  async fileExists (directoryHandle, fileName) {
    try {
      await directoryHandle.getFileHandle(fileName)
      return true
    } catch (error) {
      // File doesn't exist if this throws an error
      return false
    }
  }

  // --- Helpers ---

  // DJI Folder Name: "RootFolderName/2025-05-02-10250 2025-05-02 08/56/35 (UTC+10)/"
  // 1. Find the first occurrence of "YYYY-MM-DD" in the folder name, or file name if no folder can be read
  // 2. Extract the "2025-05-02" part to get the date
  // 3. Fallback to the flight date if not found
  // 4. Create the target folder name using the date, flight name, billable type, and task category
  buildTargetFolderName (entries, flightDate, flightName, billableType, taskCategory) {
    let rootFolderDate = null

    // Use date from folder name
    for (const entry of entries) {
      if (entry.directory) {
        const dateMatch = entry.filename.match(/\d{4}-\d{2}-\d{2}/)
        if (dateMatch) {
          rootFolderDate = this.parseDJIFolderDate(entry.filename)
          if (rootFolderDate) {
            this.logMessage(`Found date from root folder: ${rootFolderDate.toISOString().split('T')[0]}`, 'info')
            break
          }
        }
      }
    }

    // Use date from file name
    if (!rootFolderDate) {
      for (const entry of entries) {
        const pathParts = entry.filename.split('/')
        for (let i = 0; i < pathParts.length; i++) {
          const dateMatch = pathParts[i].match(/\d{4}-\d{2}-\d{2}/)
          if (dateMatch) {
            rootFolderDate = this.parseDJIFolderDate(pathParts[i])
            if (rootFolderDate) {
              this.logMessage(`Found date from file path: ${rootFolderDate.toISOString().split('T')[0]}`, 'info')
              break
            }
          }
        }
        if (rootFolderDate) break
      }
    }

    // Fallback to flight date.
    const folderDate = rootFolderDate || new Date(flightDate)

    const folderName = this.generateFolderName(folderDate, flightName, billableType, taskCategory)
    const sanitizedFolderName = this.sanitizeFileName(folderName)

    return sanitizedFolderName
  }

  async buildTargetFileName (entry, flightDate, flightName, billableType, taskCategory, subFolder) {
    const ext = this.getExtension(entry.filename).toLowerCase()

    const fileDatetime = this.parseDJIFileDatetime(entry.filename) || new Date(flightDate)

    // Base file date should be the flight date
    const datetimeKey = this.generateDateTimeKey(fileDatetime)

    // Create final filename
    const baseFileName = this.generateFileName(datetimeKey, flightName, billableType, taskCategory, '', ext)
    let sanitizedBaseName = this.sanitizeFileName(baseFileName)

    let counter = 0

    while (await this.fileExists(subFolder, sanitizedBaseName)) {
      counter++
      sanitizedBaseName = this.sanitizeFileName(
        this.generateFileName(datetimeKey, flightName, billableType, taskCategory, `(${counter})`, ext)
      )
    }

    return sanitizedBaseName
  }

  // Parse DJI folder date from folder name
  // Example: "RootFolderRandomName/2025-05-02-10250 2025-05-02 08/56/35 (UTC+10)/"
  parseDJIFolderDate (folderName) {
    try {
      const dateMatch = folderName.match(/(\d{4})-(\d{2})-(\d{2})/)
      if (dateMatch) {
        const [, year, month, day] = dateMatch
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
      }
      return null
    } catch (error) {
      this.logMessage(`Error parsing DJI folder date: ${error.message}`, 'warning')
      return null
    }
  }

  parseDJIFileDatetime (fileName) {
    try {
      // Extract file name from path
      const baseName = fileName.split('/').pop()

      // Check for DJI file naming pattern like "DJI_20250502100043_0009_V.jpeg"
      // or DAT file pattern like "DJI_2025_05_02_09_57_27_7CTDM2100BQ58L.DAT"
      const djiMatch = baseName.match(/DJI_(\d{4})_?(\d{2})_?(\d{2})_?(\d{2})_?(\d{2})_?(\d{2})/)

      if (djiMatch) {
        const [, year, month, day, hour, minute, second] = djiMatch
        return new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hour),
          parseInt(minute),
          parseInt(second)
        )
      }

      return null
    } catch (error) {
      this.logMessage(`Error parsing DJI file name: ${error.message}`, 'warning')
      return null
    }
  }

  parseFlightDate () {
    try {
      if (this.flightDateValue && this.flightDateValue.trim() !== '') {
        const date = new Date(this.flightDateValue)
        return isNaN(date.getTime()) ? null : date
      }
      return null
    } catch (error) {
      console.warn('Invalid flight date:', error)
      return null
    }
  }

  generateFolderName (folderDate, flightName, billableType, taskCategory) {
    return `${folderDate.getFullYear()}-${this.pad(folderDate.getMonth() + 1)}-${this.pad(folderDate.getDate())}-${flightName}-${billableType}-${taskCategory}`
  }

  // Generate datetime key including time (for image/video files with EXIF)
  generateDateTimeKey (dateTime) {
    return `${dateTime.getFullYear()}-${this.pad(dateTime.getMonth() + 1)}-${this.pad(dateTime.getDate())}--${this.pad(dateTime.getHours())}-${this.pad(dateTime.getMinutes())}-${this.pad(dateTime.getSeconds())}`
  }

  generateFileName (datetimeKey, flightName, billableType, taskCategory, suffix, extension) {
    return `${datetimeKey}${suffix}-${flightName}-${billableType}-${taskCategory}${extension}`
  }

  resetState () {
    // Reset counters and UI
    this.clearTimer()
    this.processing = false
    this.successfulFiles = 0
    this.errorFiles = 0

    this.progressCardTarget.style.display = 'none'
    this.statusBadgeTarget.textContent = ''
    this.statusBadgeTarget.classList.add('d-none')
    this.progressBarTarget.style.width = '0%'
    this.totalFilesTarget.textContent = '0'
    this.processedFilesTarget.textContent = '0'
    this.successCountTarget.textContent = '0'
    this.errorCountTarget.textContent = '0'
    this.elapsedTimeTarget.textContent = '00:00:00'
    this.destinationPathTarget.textContent = ''
    this.clearLog()

    this.setProcessingState(false)
    this.validateForm()
  }

  setProcessingState (isProcessing) {
    // Enable/disable form elements
    this.zipInputTarget.disabled = isProcessing
    this.billableTypeTargets.forEach(radio => { radio.disabled = isProcessing })
    this.taskCategoryTargets.forEach(radio => { radio.disabled = isProcessing })
    this.processButtonTarget.disabled = isProcessing
    this.selectDestinationButtonTarget.disabled = isProcessing

    if (isProcessing) {
      this.processButtonTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Processing...'
    } else {
      this.processButtonTarget.innerHTML = 'Start Process'
      this.validateForm()
    }
  }

  updateProgress (current, total) {
    const percentage = total === 0 ? 100 : Math.round((current / total) * 100)
    this.progressBarTarget.style.width = `${percentage}%`
    this.progressBarTarget.setAttribute('aria-valuenow', percentage)
    this.progressStatsTarget.textContent = `${current}/${total} (${percentage}%)`
    this.processedFilesTarget.textContent = current
  }

  updateStatus (message, type) {
    this.statusBadgeTarget.textContent = message
    this.statusBadgeTarget.className = `badge text-white bg-${type}`
    this.statusBadgeTarget.classList.remove('d-none')
    this.logMessage(`Status: ${message}`, 'info')
  }

  startTimer () {
    this.clearTimer()
    this.startTime = new Date()
    this.timerInterval = setInterval(() => this.updateTimer(), 1000)
  }

  stopTimer () {
    this.clearTimer()
    this.updateTimer()
  }

  clearTimer () {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  updateTimer () {
    if (!this.startTime) return
    const now = new Date()
    const elapsedMs = now - this.startTime
    const seconds = Math.floor((elapsedMs / 1000) % 60)
    const minutes = Math.floor((elapsedMs / (1000 * 60)) % 60)
    const hours = Math.floor(elapsedMs / (1000 * 60 * 60))
    this.elapsedTimeTarget.textContent = `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`
  }

  logMessage (msg, level = 'info') {
    const timestamp = new Date().toLocaleTimeString()
    let className = ''
    switch (level) {
      case 'error':
        className = 'text-danger'
        break
      case 'warning':
        className = 'text-warning'
        break
      case 'success':
        className = 'text-success'
        break
    }
    const logLine = document.createElement('div')
    logLine.className = className
    logLine.textContent = `[${timestamp}] ${msg}`
    this.logTarget.appendChild(logLine)
    this.logTarget.scrollTop = this.logTarget.scrollHeight
  }

  showError (message) {
    this.logMessage(message, 'error')
    alert(message)
  }

  getSelectedRadioValue (targets) {
    for (const radio of targets) {
      if (radio.checked) return radio.value
    }
    return targets[0].value
  }

  pad (n) {
    return n.toString().padStart(2, '0')
  }

  getExtension (filename) {
    const index = filename.lastIndexOf('.')
    return index !== -1 ? filename.substring(index) : ''
  }
}
